import { useRef, useEffect } from 'react'
import {
	useCarousel,
	useCarouselGestures,
	useCarouselKeyboard,
} from '#app/hooks/use-carousel.ts'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './button.tsx'
import { Icon } from './icon.tsx'

export interface CarouselImage {
	src: string
	alt: string
	rounded?: 'default' | 'large'
}

export interface CarouselProps {
	images: CarouselImage[]
	className?: string
	showControls?: boolean
	autoPlay?: boolean
	autoPlayInterval?: number
	loop?: boolean
}

export function Carousel({
	images,
	className,
	showControls = true,
	autoPlay = false,
	autoPlayInterval = 3000,
	loop = true,
}: CarouselProps) {
	const carouselRef = useRef<HTMLDivElement>(null)

	// Use carousel hook for state management
	const {
		currentIndex,
		goToNext,
		goToPrevious,
		goToSlide,
		canGoNext,
		canGoPrevious,
	} = useCarousel({
		totalSlides: images.length,
		autoPlay,
		autoPlayInterval,
		loop,
	})

	// Add swipe gesture support
	const { handleTouchStart, handleTouchMove, handleTouchEnd } =
		useCarouselGestures({
			onSwipeLeft: goToNext,
			onSwipeRight: goToPrevious,
			enabled: true,
		})

	// Add keyboard navigation
	const { handleKeyDown } = useCarouselKeyboard({
		onNext: goToNext,
		onPrevious: goToPrevious,
		enabled: true,
	})

	// Scroll to current index
	useEffect(() => {
		if (!carouselRef.current) return
		const carousel = carouselRef.current
		// Mobile: 250px + 24px gap, Desktop: 280px + 32px gap
		const isMobile = window.innerWidth < 768
		const imageWidth = isMobile ? 250 + 24 : 280 + 32
		carousel.scrollTo({
			left: currentIndex * imageWidth,
			behavior: 'smooth',
		})
	}, [currentIndex])

	return (
		<div
			className={cn('relative', className)}
			onKeyDown={handleKeyDown}
			tabIndex={0}
		>
			{/* Carousel container */}
			<div
				ref={carouselRef}
				className="flex gap-6 overflow-x-auto scroll-smooth md:gap-8"
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				style={{
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
				}}
			>
				{images.map((image, idx) => (
					<div
						key={idx}
						className={cn(
							'h-[318px] w-[250px] flex-shrink-0 overflow-hidden md:h-[356px] md:w-[280px]',
							image.rounded === 'large' || idx === 0
								? 'rounded-[24px]'
								: 'rounded-[16px]',
						)}
					>
						<img
							src={image.src}
							alt={image.alt}
							className="h-full w-full object-cover"
							draggable={false}
						/>
					</div>
				))}
			</div>

			{/* Navigation controls */}
			{showControls && images.length > 1 && (
				<>
					{/* Previous button */}
					{canGoPrevious && (
						<Button
							variant="secondary"
							size="m"
							appearance="ghost"
							iconOnly
							leftIcon={<Icon name="arrow-left" size="md" />}
							onClick={goToPrevious}
							className="absolute top-1/2 left-4 z-10 -translate-y-1/2 bg-white/90 shadow-lg hover:bg-white"
							aria-label="Previous image"
						/>
					)}

					{/* Next button */}
					{canGoNext && (
						<Button
							variant="secondary"
							size="m"
							appearance="ghost"
							iconOnly
							leftIcon={<Icon name="arrow-right" size="md" />}
							onClick={goToNext}
							className="absolute top-1/2 right-4 z-10 -translate-y-1/2 bg-white/90 shadow-lg hover:bg-white"
							aria-label="Next image"
						/>
					)}

					{/* Indicators */}
					<div className="mt-6 flex justify-center gap-2">
						{images.map((_, idx) => (
							<button
								key={idx}
								onClick={() => goToSlide(idx)}
								className={cn(
									'h-2 rounded-full transition-all',
									idx === currentIndex
										? 'w-8 bg-blue-500'
										: 'w-2 bg-neutral-400 hover:bg-neutral-600',
								)}
								aria-label={`Go to image ${idx + 1}`}
							/>
						))}
					</div>
				</>
			)}
		</div>
	)
}
